import Modal from "@via/components/modal.tsx";
import { type FC, useState } from "react";
import {
  MessageBanner,
  BannerType,
} from "@via/components/message-banner/message-banner.tsx";
import { CopyableText } from "@via/components/copyable-text/copyable-text.tsx";
import {
  ChevronDownIcon,
  ChevronUpIcon,
  ClipboardDocumentIcon,
  CheckIcon,
} from "@heroicons/react/24/outline";
import { HorizontalTabs } from "@via/components/tabs.tsx";
import { useNotifications } from "@via/hooks/useNotifications.tsx";
import { NOTIFICATION } from "@via/context/notifications-context.tsx";
import Config from "@via/utils/config.tsx";
import { PersonalAccessTokenScope, PersonalAccessTokenUsage } from "common";
import AccessScopeBadge from "@via/pages/account/profile/personal-access-tokens/components/access-scope-badge";
import SelectComponent from "@via/components/select/select.tsx";
import { isLocalhost } from "@via/utils/helpers.tsx";
import { useConfigurations } from "@via/api/configuration.tsx";
import {
  PATCodeType,
  PAT_MCP_TABS,
  PAT_SDK_TABS,
  PAT_MCP_IDE_OPTIONS,
} from "@via/utils/constants.tsx";
import {
  getCodeForType,
  buildHostnameHeader,
} from "@via/pages/account/profile/personal-access-tokens/utils/code-generators.ts";

interface TokenDisplayModalProps {
  token: string;
  expiresAt: string;
  usageType: PersonalAccessTokenUsage;
  scope: PersonalAccessTokenScope | null;
  onClose: () => void;
}

const TokenDisplayModal: FC<TokenDisplayModalProps> = ({
  token,
  expiresAt,
  usageType,
  scope,
  onClose,
}) => {
  const { setNotification } = useNotifications();
  const [showCode, setShowCode] = useState(true);
  const isMcpToken = usageType === PersonalAccessTokenUsage.MCP;
  const [codeType, setCodeType] = useState<PATCodeType>(
    isMcpToken ? PATCodeType.Mcp : PATCodeType.PythonSdk
  );
  const [copied, setCopied] = useState(false);
  const [mcpIde, setMcpIde] = useState<(typeof PAT_MCP_IDE_OPTIONS)[number]>(
    PAT_MCP_IDE_OPTIONS[0]
  );

  // Use Config.HOST which includes the base path (e.g., /beta/) from runtime config
  const currentHostname = Config.HOST || window.location.origin;
  const isLocalhostEnv = isLocalhost(currentHostname);

  // Get MCP URL from backend configuration
  const { data: configurations } = useConfigurations();
  // For localhost: use currentHostname/mcp
  // For production: use MCP_URL from config (separate subdomain)
  const mcpUrl = isLocalhostEnv
    ? `${currentHostname}/mcp`
    : configurations?.MCP_URL || `${currentHostname}/mcp`;

  const codeGeneratorParams = {
    token,
    hostname: currentHostname,
    mcpUrl,
    hostnameHeader: buildHostnameHeader(isLocalhostEnv, currentHostname),
  };

  const handleCopyCode = async () => {
    const code = getCodeForType(codeType, codeGeneratorParams, mcpIde.value);

    if (navigator?.clipboard) {
      try {
        await navigator.clipboard.writeText(code);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error("Failed to copy code: ", err);
        setNotification({
          id: "copy-code-failed",
          message: "Failed to copy code",
          details: "Could not copy to clipboard. Please copy manually.",
          variant: NOTIFICATION.ERROR,
        });
      }
    } else {
      setNotification({
        id: "copy-clipboard-not-supported-error",
        message: "Clipboard not supported",
        details:
          "Clipboard API not supported in this browser. Please copy the text manually or try another browser.",
        variant: NOTIFICATION.ERROR,
      });
    }
  };

  return (
    <Modal open={true} setOpen={onClose} title="Your Personal Access Token">
      <div>
        <MessageBanner
          messages="Make sure to copy your token now. You won't be able to see it again!"
          type={BannerType.Warning}
          title="Important"
        />
        <div className="mt-4">
          <CopyableText value={token} className="[&>span]:max-w-[600px]" />
          <div className="mt-3 text-sm text-gray-500">
            <p>
              <strong>Expires:</strong>{" "}
              {new Date(expiresAt).toLocaleDateString()}
            </p>
            <p className="mt-1 flex items-center gap-2">
              <strong>Access:</strong> <AccessScopeBadge scope={scope} />
            </p>
          </div>
        </div>

        {/* Usage Examples Section */}
        <div className="mt-4 mb-6 border-t border-gray-200 pt-3">
          <button
            onClick={() => setShowCode(!showCode)}
            className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900">
            {showCode ? (
              <>
                <ChevronUpIcon className="h-4 w-4" />
                Hide Usage Examples
              </>
            ) : (
              <>
                <ChevronDownIcon className="h-4 w-4" />
                Show Usage Examples
              </>
            )}
          </button>

          {showCode && (
            <div className="mt-3 space-y-3">
              {/* Tab Buttons */}
              <HorizontalTabs
                options={isMcpToken ? PAT_MCP_TABS : PAT_SDK_TABS}
                activeTab={codeType}
                onTabChange={setCodeType}
              />

              {/* Code Content */}
              <div className="relative">
                <textarea
                  readOnly
                  value={getCodeForType(
                    codeType,
                    codeGeneratorParams,
                    mcpIde.value
                  )}
                  className="w-full h-56 font-mono text-xs p-3 bg-gray-900 text-gray-100 rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500 whitespace-pre leading-normal"
                />
                <div className="absolute top-2 right-2 flex items-center gap-2">
                  {codeType === PATCodeType.Mcp && (
                    <div className="w-40">
                      <SelectComponent
                        name="mcpIde"
                        options={PAT_MCP_IDE_OPTIONS}
                        value={mcpIde}
                        onChange={selected =>
                          setMcpIde(
                            selected as (typeof PAT_MCP_IDE_OPTIONS)[number]
                          )
                        }
                        enableClear={false}
                        isSearchable={false}
                        placeholder="Select IDE"
                      />
                    </div>
                  )}
                  <button
                    onClick={handleCopyCode}
                    className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md transition">
                    {copied ? (
                      <>
                        <CheckIcon className="w-4 h-4" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <ClipboardDocumentIcon className="w-4 h-4" />
                        Copy Code
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default TokenDisplayModal;
